// ts-rest contract for the OPERATIONS side of Transfers (SEP-283): the ticket
// list with its stage counts, one ticket, the Ops edits, the holdings, the
// email and the stage moves.
//
// Every mutation answers with the refreshed ticket, so the page needs no second
// read. `/ops/transfer-owners` has its own prefix so it never matches `/:id`.
//
// Two routes are raw Nest and not ts-rest, because ts-rest is JSON-only:
//   POST /api/ops/transfers/:id/documents                       multipart upload
//                                                               (fields: file,
//                                                               documentType, purpose)
//   GET  /api/ops/transfers/:id/documents/:documentId/content   bytes
//
// The unread updates of one Operations user (SEP-317 section 6) are under
// `/ops/transfer-updates`, so the paths never match `/:id`. A read of a ticket
// never marks it read: the page calls `markRead` when the user opens it.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBody, errorBodyWithCode } from '../schemas/common';
import {
    markTransferCompleteBodySchema,
    addTransferHoldingBodySchema,
    addTransferHoldingNoteBodySchema,
    addTransferNoteBodySchema,
    changeTransferHoldingStatusBodySchema,
    confirmTransferHoldingsBodySchema,
    editTransferAnswersBodySchema,
    editTransferNoteBodySchema,
    changeTransferOwnerBodySchema,
    changeTransferStageBodySchema,
    opsTransferDetailSchema,
    opsTransferListQuerySchema,
    opsTransferListResponseSchema,
    opsTransferOwnerOptionsSchema,
    opsTransferReadResponseSchema,
    opsTransferUpdatesResponseSchema,
    recallTransferBodySchema,
    sendTransferEmailBodySchema,
    setHappyFoxRefBodySchema,
    setOperationsAnswersBodySchema,
    setTransferTypeBodySchema,
    transferEmailPreviewBodySchema,
    transferEmailPreviewSchema,
    transferEmailRefusalSchema,
    transferEmailTemplateListSchema,
    transferHoldingConfirmationBodySchema,
    transferHoldingInclusionBodySchema,
} from '../schemas/transfers';

const c = initContract();

const idParams = z.object({ id: z.string() });
const holdingParams = z.object({ id: z.string(), holdingId: z.string() });
const noteParams = z.object({ id: z.string(), noteId: z.string() });

export const transfersOpsContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/ops/transfers',
            query: opsTransferListQuerySchema,
            responses: { 200: opsTransferListResponseSchema, 403: errorBody },
            summary: 'The transfer tickets of one tab, with the search and the stage counts',
        },
        owners: {
            method: 'GET',
            path: '/ops/transfer-owners',
            responses: { 200: opsTransferOwnerOptionsSchema, 403: errorBody },
            summary: 'The people a ticket can be given to',
        },
        updates: {
            method: 'GET',
            path: '/ops/transfer-updates',
            responses: { 200: opsTransferUpdatesResponseSchema, 403: errorBody },
            summary: 'The tickets with an update that the signed-in user has not read',
        },
        markAllRead: {
            method: 'POST',
            path: '/ops/transfer-updates/read-all',
            body: z.object({}),
            responses: { 200: opsTransferReadResponseSchema, 403: errorBody },
            summary: 'Mark every ticket read for the signed-in user',
        },
        markRead: {
            method: 'POST',
            path: '/ops/transfers/:id/read',
            pathParams: idParams,
            body: z.object({}),
            responses: { 200: opsTransferReadResponseSchema, 403: errorBody, 404: errorBody },
            summary: 'Mark one ticket read for the signed-in user',
        },
        get: {
            method: 'GET',
            path: '/ops/transfers/:id',
            pathParams: idParams,
            responses: { 200: opsTransferDetailSchema, 403: errorBody, 404: errorBody },
            summary: 'One transfer ticket, in full',
        },
        setTransferType: {
            method: 'PATCH',
            path: '/ops/transfers/:id/transfer-type',
            pathParams: idParams,
            body: setTransferTypeBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Set the Transfer Type of a ticket',
        },
        setHappyFoxRef: {
            method: 'PATCH',
            path: '/ops/transfers/:id/happy-fox-ref',
            pathParams: idParams,
            body: setHappyFoxRefBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Set the Happy Fox Ref of a ticket',
        },
        setOperationsAnswers: {
            method: 'PATCH',
            path: '/ops/transfers/:id/operations-answers',
            pathParams: idParams,
            body: setOperationsAnswersBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Record the Operations answers (Q5d and Q6a)',
        },
        changeStage: {
            method: 'POST',
            path: '/ops/transfers/:id/stage',
            pathParams: idParams,
            body: changeTransferStageBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Move a ticket to another open stage',
        },
        changeOwner: {
            method: 'POST',
            path: '/ops/transfers/:id/owner',
            pathParams: idParams,
            body: changeTransferOwnerBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Change the current owner of a ticket',
        },
        addNote: {
            method: 'POST',
            path: '/ops/transfers/:id/notes',
            pathParams: idParams,
            body: addTransferNoteBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Add a note, internal or visible to the Desk',
        },
        editNote: {
            method: 'PATCH',
            path: '/ops/transfers/:id/notes/:noteId',
            pathParams: noteParams,
            body: editTransferNoteBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Edit the text of a ticket note or a holding note (SEP-359)',
        },
        removeNote: {
            method: 'POST',
            path: '/ops/transfers/:id/notes/:noteId/remove',
            pathParams: noteParams,
            body: z.object({}),
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Remove a ticket note or a holding note from the notes lists (SEP-359)',
        },
        editAnswers: {
            method: 'PATCH',
            path: '/ops/transfers/:id/answers',
            pathParams: idParams,
            body: editTransferAnswersBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Edit answers of the submitted checklist, with a note (SEP-359)',
        },
        setHoldingInclusion: {
            method: 'POST',
            path: '/ops/transfers/:id/holdings/:holdingId/inclusion',
            pathParams: holdingParams,
            body: transferHoldingInclusionBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Include or exclude one holding line',
        },
        confirmHolding: {
            method: 'POST',
            path: '/ops/transfers/:id/holdings/:holdingId/confirmation',
            pathParams: holdingParams,
            body: transferHoldingConfirmationBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Confirm the quantity and value of one holding line',
        },
        addHolding: {
            method: 'POST',
            path: '/ops/transfers/:id/holdings',
            pathParams: idParams,
            body: addTransferHoldingBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Add one holding line to a transfer in, from the holdings file',
        },
        confirmHoldings: {
            method: 'POST',
            path: '/ops/transfers/:id/holdings/confirmation',
            pathParams: idParams,
            body: confirmTransferHoldingsBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Confirm many holding lines in one act',
        },
        changeHoldingStatus: {
            method: 'POST',
            path: '/ops/transfers/:id/holdings/:holdingId/status',
            pathParams: holdingParams,
            body: changeTransferHoldingStatusBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Set the status of one holding line',
        },
        addHoldingNote: {
            method: 'POST',
            path: '/ops/transfers/:id/holdings/:holdingId/notes',
            pathParams: holdingParams,
            body: addTransferHoldingNoteBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Add a note to one holding line, internal or visible to the Desk',
        },
        acknowledgeHoldingResponse: {
            method: 'POST',
            path: '/ops/transfers/:id/holdings/:holdingId/acknowledge',
            pathParams: holdingParams,
            body: z.object({}),
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Review the response of the IM on one holding line (SEP-359)',
        },
        emailTemplates: {
            method: 'GET',
            path: '/ops/transfers/:id/email-templates',
            pathParams: idParams,
            responses: { 200: transferEmailTemplateListSchema, 403: errorBody, 404: errorBody },
            summary: 'The email templates that fit the ticket',
        },
        previewEmail: {
            method: 'POST',
            path: '/ops/transfers/:id/email-preview',
            pathParams: idParams,
            body: transferEmailPreviewBodySchema,
            responses: {
                200: transferEmailPreviewSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'The fields, the subject, the body, the mailboxes and the expected attachments of a template. Nothing is sent.',
        },
        sendEmail: {
            method: 'POST',
            path: '/ops/transfers/:id/emails',
            pathParams: idParams,
            body: sendTransferEmailBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: transferEmailRefusalSchema,
                403: errorBody,
                404: errorBody,
                409: transferEmailRefusalSchema,
            },
            summary:
                'Send an email from the ticket and keep it. A blank required field is a 400; a missing expected attachment is a 409 unless the body confirms it.',
        },
        markComplete: {
            method: 'POST',
            path: '/ops/transfers/:id/complete',
            pathParams: idParams,
            body: markTransferCompleteBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Mark a ticket complete',
        },
        recall: {
            method: 'POST',
            path: '/ops/transfers/:id/recall',
            pathParams: idParams,
            body: recallTransferBodySchema,
            responses: {
                200: opsTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Recall a completed ticket to an open stage',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
